import {
  Button,
  color,
  InputGroup,
  InputSelect,
  NO_REGEX,
  SWIFT_CODE_REGEX,
} from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStyle } from "../../styles/BankDetailsCss";
import { useDispatch, useSelector } from "react-redux";
import {
  setBankNameRedux,
  setAccountHolderNameRedux,
  setAccountNoRedux,
  setSwiftCodeReduct,
  setAccountTypeRedux,
} from "../../features/merchant/bankSlice";
import { RootState } from "../../store";
import { toast, ToastContainer } from "react-toastify";
import { bankAccountTypes } from "../../constants/bank";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";

const BorrowerBank: FC = () => {
  const { classes } = useStyle();
  const Navigate = useNavigate();

  const { company } = useSelector((state: RootState) => state.borBiz);

  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);

  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [swiftCode, setSwiftCode] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");

  const [error, setError] = useState({
    bankName: "",
    accountHolderName: "",
    swiftCode: "",
    accountNumber: "",
    accountType: "",
  });
  const dispatch = useDispatch();

  const handleBack = () => {
    saveDraft(false);
    dispatch(setBankNameRedux({ bankName }));
    dispatch(setAccountNoRedux({ accountNumber }));
    dispatch(setAccountHolderNameRedux({ accountHolderName }));
    dispatch(setAccountTypeRedux({ accountType }));
    dispatch(setSwiftCodeReduct({ swiftCode }));

    Navigate("/bor-rep");
  };

  const handleNumericInputChange = (actualOnChange: any) => (value: any) => {
    const numericValue = value.replace(NO_REGEX, "");
    actualOnChange(numericValue);
  };

  const handleNext = () => {
    saveDraft(false);
    const customError = { ...error };
    let valid = true;

    if (!bankName) {
      customError.bankName = "Bank Name is required";
      valid = false;
    }
    if (!accountNumber) {
      customError.accountNumber = "Account Number is required";
      valid = false;
    }
    if (!accountType) {
      customError.accountType = "Account Type is required";
      valid = false;
    }
    if (!swiftCode) {
      customError.swiftCode = "Swift Code is required";
      valid = false;
    }
    if (!accountHolderName) {
      customError.accountHolderName = "Account Holder Name is required";
      valid = false;
    }
    if (company) {
      if (accountHolderName !== company) {
        valid = false;
        toast.warn("Account Holder Name should be the same as company name");
      }
    }

    if (valid) {
      setTimeout(() => {
        Navigate("/bor-doc");
      }, 500);
    } else {
      setError(customError);
    }
  };

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token,
      };

      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await fetchDraft(parseInt(userId));

          if (data) {
            setBankName(data.bankName);
            setAccountType(data.accountType);
            setAccountNumber(data.accountNumber);
            setAccountHolderName(data.accountHolderName);
            setSwiftCode(data.swiftCode);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId]);

  const saveDraft = (showToast: Boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        bankName: bankName,
        accountNumber: accountNumber,
        accountHolderName: accountHolderName,
        accountType: accountType,
        swiftCode: swiftCode,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <InputGroup
        isErrored={error.bankName}
        onChange={(value: any) => {
          error.bankName = "";
          setBankName(value);
        }}
        labelText="Bank Name"
        id="bank_name"
        value={bankName}
      />
      <InputGroup
        isErrored={error.accountNumber}
        onChange={handleNumericInputChange((value: any) => {
          error.accountNumber = "";
          setAccountNumber(value.replace(NO_REGEX, ""));
        })}
        labelText="Bank Account Number"
        id="account_number"
        value={accountNumber}
      />
      <InputSelect
        isErrored={error.accountType}
        onChange={(value: any) => {
          error.accountType = "";
          setAccountType(value);
        }}
        labelText="Account type"
        id="accountType"
        value={accountType}
        data={bankAccountTypes}
      />
      <InputGroup
        isErrored={error.swiftCode}
        onChange={(value: any) => {
          error.swiftCode = "";
          setSwiftCode(value.replace(SWIFT_CODE_REGEX, ""));
        }}
        labelText="Bank Swift Code"
        value={swiftCode}
        id="swift_code"
      />
      <BankAccNameDisclaimer />
      <InputGroup
        isErrored={error.accountHolderName}
        onChange={(value: any) => {
          error.accountHolderName = "";
          setAccountHolderName(value);
        }}
        labelText="Account Holder Name (should match with institution name)"
        value={accountHolderName}
        id="account_holder_name"
      />
      <div className={classes.buttonDiv}>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleBack}
            backgroundColor="#ffff"
            textColor="#338F86"
            children="Back"
            border="1px solid #338F86"
            width="100%"
          />
        </div>
        <ToastContainer />
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleNext}
            textColor="#ffff"
            backgroundColor="#338F86"
            children="Next"
            width="100%"
          />
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={saveDraft}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default BorrowerBank;
